import { useSelection } from '@/app/SelectionContext';
import { useCompanyOverview } from '@/app/hooks';
import { PageHeader } from '@/components/PageHeader';
import { ExportMenu } from '@/components/ExportMenu';
import { KPICard } from '@/components/KPICard';
import { KpiGridSkeleton } from '@/components/LoadingScreen';
import { ErrorState } from '@/components/ErrorState';
import { NoFinancialData } from '@/components/NoFinancialData';
import { Card, SectionTitle } from '@/components/Card';
import { Badge } from '@/components/Badge';
import {
  ChartCard,
  ExpenseStackChart,
  ProfitTrendChart,
  RevenueTrendChart,
  SingleSeriesChart,
} from '@/components/charts';
import { buildPoints } from '@/utils/series';
import {
  MONTHS_RU,
  formatAmd,
  formatPercent,
  periodShortLabel,
  toNumber,
} from '@/utils/format';
import { v } from '@/theme/tokens';
import type { CompanyMonthlyPoint, CompanyYtd } from '@/types/schemas';

/** `positiveIsGood` follows the project-wide rule: a negative value is always
 * the danger colour, a positive one is green only where that genuinely means
 * a good result — profit and the residual, never an expense. */
const YTD_MONEY_KPIS = [
  ['restaurant_revenue', 'Выручка ресторанов', true],
  ['restaurant_gross_profit', 'Валовая прибыль', true],
  ['restaurant_operating_profit', 'Операционная прибыль', true],
  ['restaurant_net_profit', 'Чистая прибыль', true],
  ['restaurant_expenses', 'Все расходы ресторанов', false],
  ['group_payroll', 'ФОТ группы', false],
  ['production_cost', 'Затраты производства', false],
  ['extraordinary_expenses', 'Внеплановые расходы', false],
] as const;

const YTD_RATIO_KPIS = [
  ['restaurant_gross_margin', 'Валовая маржа'],
  ['restaurant_operating_margin', 'Операционная маржа'],
  ['restaurant_net_margin', 'Чистая маржа'],
] as const;

const INVERTED = new Set<string>([
  'restaurant_expenses',
  'group_payroll',
  'production_cost',
  'extraordinary_expenses',
]);

/** "Январь–Июль 2026" — the span actually covered, so an unconfirmed August
 * never appears in the heading. */
function ytdSpanLabel(ytd: CompanyYtd): string {
  const from = MONTHS_RU[ytd.from_month - 1] ?? ytd.from_month;
  const to = MONTHS_RU[ytd.to_month - 1] ?? ytd.to_month;
  return ytd.from_month === ytd.to_month ? `${to} ${ytd.year}` : `${from}–${to} ${ytd.year}`;
}

function seriesOf(points: CompanyMonthlyPoint[], key: keyof CompanyMonthlyPoint): (number | null)[] {
  return points.map((point) => toNumber(point[key] as string | null));
}

export function CompanyPage() {
  const { selectedPeriod, units } = useSelection();
  const query = useCompanyOverview(selectedPeriod?.id);

  const restaurants = units.filter((u) => u.type === 'RESTAURANT').length;
  const kitchens = units.filter((u) => u.type === 'PRODUCTION_KITCHEN').length;

  const data = query.data;
  const ytd = data?.ytd ?? null;
  const points = data?.monthly_series ?? [];
  const hasData =
    data != null &&
    [data.restaurant_revenue, data.restaurant_net_profit, data.production_cost].some(
      (value) => value !== null,
    );

  const subtitle = ytd
    ? `Итоги года · ${ytdSpanLabel(ytd)} · ${restaurants} ресторан · ${kitchens} производство`
    : 'Консолидированный обзор';

  return (
    <div style={{ animation: 'fade-in 0.35s ease' }}>
      <PageHeader
        title="Вся компания"
        subtitle={subtitle}
        actions={<ExportMenu report="company" params={{ periodId: selectedPeriod?.id }} />}
      />

      {query.isPending && <KpiGridSkeleton count={6} />}

      {query.error && (
        <ErrorState
          message={query.error instanceof Error ? query.error.message : 'Ошибка загрузки'}
          onRetry={() => void query.refetch()}
        />
      )}

      {data && !hasData && <NoFinancialData scope="company" />}

      {data && hasData && (
        <>
          {ytd && <YtdCards ytd={ytd} />}
          {points.length > 0 && <CompanyCharts points={points} />}
          {kitchens > 0 && ytd && <ProductionSection ytd={ytd} points={points} data={data} />}
          <ConsolidationCard data={data} />
        </>
      )}
    </div>
  );
}

function YtdCards({ ytd }: { ytd: CompanyYtd }) {
  const residual = toNumber(ytd.residual);
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))',
          gap: 14,
          marginBottom: 14,
        }}
      >
        {YTD_MONEY_KPIS.map(([key, label, positiveIsGood]) => {
          const raw = toNumber(ytd[key]);
          return (
            <KPICard
              key={key}
              label={label}
              value={formatAmd(raw)}
              rawValue={raw}
              positiveIsGood={positiveIsGood}
              change={null}
              invert={INVERTED.has(key)}
              caption={`за ${ytd.months} мес.`}
            />
          );
        })}
        {YTD_RATIO_KPIS.map(([key, label]) => {
          const raw = toNumber(ytd[key]);
          return (
            <KPICard
              key={key}
              label={label}
              value={formatPercent(raw)}
              rawValue={raw}
              positiveIsGood
              change={null}
              caption={`за ${ytd.months} мес.`}
            />
          );
        })}

        {/* «Остаток» sits in the same grid as every other KPI and keeps the
            same geometry — it stands out by its semantic border and value
            colour, not by being a wider block. */}
        <KPICard
          label="Остаток"
          value={formatAmd(residual)}
          rawValue={residual}
          positiveIsGood
          emphasize
          change={null}
          caption={`за ${ytd.months} мес.`}
        />
      </div>

      <div style={{ fontSize: 11.5, color: v('ink-faint'), marginBottom: 16 }}>
        Маржа за год — сумма прибыли делённая на сумму выручки, а не среднее месячных процентов.
      </div>
    </>
  );
}

function CompanyCharts({ points }: { points: CompanyMonthlyPoint[] }) {
  const labels = points.map((point) => periodShortLabel(point.month));

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))',
        gap: 16,
        marginBottom: 16,
      }}
    >
      <ChartCard title="Выручка по месяцам">
        <RevenueTrendChart
          data={buildPoints(labels, { 'Выручка': seriesOf(points, 'restaurant_revenue') })}
        />
      </ChartCard>

      <ChartCard title="Прибыль по месяцам">
        <ProfitTrendChart
          data={buildPoints(labels, {
            'Валовая': seriesOf(points, 'restaurant_gross_profit'),
            'Операционная': seriesOf(points, 'restaurant_operating_profit'),
            'Чистая': seriesOf(points, 'restaurant_net_profit'),
          })}
        />
      </ChartCard>

      <ChartCard title="Расходы по месяцам">
        <ExpenseStackChart
          data={buildPoints(labels, {
            'Расходы ресторанов': seriesOf(points, 'restaurant_expenses'),
            'Затраты производства': seriesOf(points, 'production_cost'),
          })}
          series={[
            { key: 'Расходы ресторанов', color: 'var(--chart-blue)' },
            { key: 'Затраты производства', color: 'var(--chart-violet)' },
          ]}
        />
      </ChartCard>

      <ChartCard title="ФОТ группы по месяцам">
        <SingleSeriesChart
          data={buildPoints(labels, { 'ФОТ группы': seriesOf(points, 'group_payroll') })}
          name="ФОТ группы"
          color="var(--chart-teal)"
        />
      </ChartCard>

      <ChartCard title="Внеплановые расходы по месяцам">
        <SingleSeriesChart
          data={buildPoints(labels, {
            'Внеплановые расходы': seriesOf(points, 'extraordinary_expenses'),
          })}
          name="Внеплановые расходы"
          color="var(--chart-amber)"
        />
      </ChartCard>

      <ChartCard title="Остаток по месяцам">
        <SingleSeriesChart
          data={buildPoints(labels, { 'Остаток': seriesOf(points, 'residual') })}
          name="Остаток"
          color="var(--chart-slate)"
        />
      </ChartCard>
    </div>
  );
}

/** Production is a cost center: spend only, reported apart from the
 * restaurant P&L. It has no revenue, profit or margin by construction. */
function ProductionSection({
  ytd,
  points,
  data,
}: {
  ytd: CompanyYtd;
  points: CompanyMonthlyPoint[];
  data: NonNullable<ReturnType<typeof useCompanyOverview>['data']>;
}) {
  const kitchens = data.units.filter((u) => u.business_unit.type === 'PRODUCTION_KITCHEN');
  if (kitchens.length === 0) return null;

  return (
    <Card style={{ marginBottom: 16 }}>
      <SectionTitle right={<Badge tone="neutral">производственный цех</Badge>}>
        {kitchens.length === 1
          ? `Производство / ${kitchens[0].business_unit.name}`
          : 'Производство'}
      </SectionTitle>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'baseline' }}>
        <div>
          <div style={{ fontSize: 12, color: v('ink-muted'), marginBottom: 4 }}>
            Затраты производства за {ytd.months} мес.
          </div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {formatAmd(toNumber(ytd.production_cost))}
          </div>
        </div>
        {kitchens.map((kitchen) => (
          <div key={kitchen.business_unit.id}>
            <div style={{ fontSize: 12, color: v('ink-muted'), marginBottom: 4 }}>
              {kitchen.business_unit.name} · выбранный месяц
            </div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              {formatAmd(toNumber(kitchen.production_cost))}
            </div>
          </div>
        ))}
      </div>

      <p style={{ fontSize: 12, color: v('ink-muted'), marginBottom: 0, marginTop: 12 }}>
        Производственный цех — центр затрат. Выручка, прибыль и маржа для него не рассчитываются:
        показываются только расходы.
      </p>

      {points.length > 0 && (
        <div style={{ height: 220, marginTop: 12 }} aria-label="Затраты производства по месяцам" role="img">
          <SingleSeriesChart
            data={buildPoints(
              points.map((point) => periodShortLabel(point.month)),
              { 'Затраты производства': seriesOf(points, 'production_cost') },
            )}
            name="Затраты производства"
            color="var(--chart-violet)"
          />
        </div>
      )}
    </Card>
  );
}

/** The selected month's allocation state — deliberately not year-to-date:
 * allocation is posted per period. */
function ConsolidationCard({
  data,
}: {
  data: NonNullable<ReturnType<typeof useCompanyOverview>['data']>;
}) {
  return (
    <Card>
      <SectionTitle
        right={
          <Badge
            tone={
              data.allocation_complete
                ? 'positive'
                : data.allocation_configured
                  ? 'warning'
                  : 'neutral'
            }
          >
            {data.allocation_complete
              ? 'распределение выполнено'
              : data.allocation_configured
                ? 'правило есть, не распределено'
                : 'распределение не настроено'}
          </Badge>
        }
      >
        Консолидация расходов · выбранный месяц
      </SectionTitle>

      {(
        [
          ['Расходы ресторанов', data.restaurant_expenses, false],
          ['Затраты цеха', data.production_cost, false],
          ['Распределено на рестораны', data.allocated_production_cost, false],
          ['Нераспределённые затраты цеха', data.unallocated_production_cost, false],
          ['Расходы группы', data.consolidated_expenses, true],
        ] as const
      ).map(([label, value, total], index, rows) => (
        <div
          key={label}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '11px 0',
            borderBottom: index < rows.length - 1 ? `1px solid ${v('line-soft')}` : 'none',
            fontSize: 13.5,
            fontWeight: total ? 700 : 400,
          }}
        >
          <span style={{ color: total ? v('ink') : v('ink-sub') }}>{label}</span>
          <span style={{ fontWeight: total ? 700 : 600 }}>{formatAmd(toNumber(value))}</span>
        </div>
      ))}

      {data.allocation_warnings.map((warning, index) => (
        <div
          key={index}
          style={{
            marginTop: 10,
            padding: '10px 12px',
            borderRadius: 10,
            background: v('warning-soft'),
            color: v('warning'),
            fontSize: 12.5,
          }}
        >
          {String(warning.message ?? warning.code)}
        </div>
      ))}

      <p style={{ fontSize: 12, color: v('ink-muted'), marginBottom: 0, marginTop: 12 }}>
        Затраты цеха — реальный расход группы. Распределение относит их на рестораны, но не
        уменьшает расходы группы, поэтому они не вычитаются автоматически. Наличие правила
        само по себе не распределяет затраты: учитываются только фактические проводки.
      </p>
    </Card>
  );
}
